import { EVENT_INTERACTION } from '@eventbrite/eds-event-card';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { startEventCardTracking } from './startEventCardTracking';
import {
    EventInteraction,
    EventInteractionContext,
    GetTrackingInformation,
    InteractionCustomEvent,
    PLACE_EVENT_INTERACTION,
} from './types';

type EventHandlerCallback = (
    event: string,
    callback: (e: InteractionCustomEvent) => void,
) => void;

export interface TrackableElement {
    addEventListener: EventHandlerCallback;
    removeEventListener: EventHandlerCallback;
    dispatchEvent: (e: CustomEvent) => void;
}

export function useTracking<T extends TrackableElement>(
    getTrackingInformation: GetTrackingInformation<any>,
) {
    useMemo(() => startEventCardTracking(), []);

    const logInteractions = useCallback(
        (e: InteractionCustomEvent) => {
            e.preventDefault();

            const detail: EventInteraction<EventInteractionContext> = {
                ...e.detail,
                ...getTrackingInformation(e.detail),
            };

            const placeEventInteractionEvent = new CustomEvent(
                PLACE_EVENT_INTERACTION,
                { detail, bubbles: true },
            );
            e.currentTarget?.dispatchEvent(placeEventInteractionEvent);
        },
        [getTrackingInformation],
    );

    const ref = useRef<T>(null);

    useEffect(() => {
        const container = ref.current;
        container?.addEventListener(EVENT_INTERACTION, logInteractions);
        return () =>
            container?.removeEventListener(EVENT_INTERACTION, logInteractions);
    }, [ref, logInteractions]);

    return { ref };
}
